import type { Ref, TextareaHTMLAttributes } from "react";
import "./text-field.css";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "aria-invalid" | "aria-describedby"> & {
  id: string;
  label: string;
  /** Una spiegazione che resta scritta sotto il campo e legata al campo, come in TextField. */
  hint?: string;
  error?: string;
  /** L'etichetta resta per gli screen reader ma non si vede. */
  hideLabel?: boolean;
  /** Un testo scritto fuori dal campo (un errore sotto la finestra, un motivo), legato anche lui al campo. */
  describedBy?: string;
  /** Il campo è sbagliato ma l'errore è scritto altrove (legalo con `describedBy`). */
  invalid?: boolean;
  /** `side`: l'etichetta a sinistra sulla stessa riga del campo (una domanda corta prima di una casella stretta). */
  labelPosition?: "top" | "side";
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
};

// TextField su più righe, per un testo libero lungo come una descrizione (UX_SYSTEM, decisione del 1° ottobre
// 2026): stesse etichetta, aiuto e semantica d'errore, che le prop native non possono sovrascrivere.
export function TextArea({ id, label, hint, error, hideLabel = false, describedBy: elsewhere, invalid = false, labelPosition = "top", className, rows = 3, ...rest }: Props) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null, elsewhere].filter(Boolean).join(" ");
  const classes = className ? `g-field__input g-field__input--multiline ${className}` : "g-field__input g-field__input--multiline";
  return (
    <div className="g-field" data-label={labelPosition === "side" ? "side" : undefined}>
      <label className={hideLabel ? "g-field__label g-visually-hidden" : "g-field__label"} htmlFor={id}>{label}</label>
      <textarea
        id={id}
        rows={rows}
        {...rest}
        className={classes}
        aria-invalid={error || invalid ? "true" : undefined}
        aria-describedby={describedBy || undefined}
      />
      {hint ? <p id={hintId} className="g-field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="g-field__error" role="alert">{error}</p> : null}
    </div>
  );
}
